import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast } from 'sonner';

import {
  type ParentDetails,
  useUpdateParentByStudentId,
} from '~entities/parent';

import { useGetCurrency } from '~/shared/api/currency-api';
import { getCountryCodeFromCurrency } from '~/shared/utils/currency-country';
import { phoneInputValueToE164 } from '~/shared/utils/phone-input-e164';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import { Button } from '~shared/shadcn/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

import { type ParentFormValues, parentSchema } from './parent-schema';

interface EditParentFormProps {
  onCancel: () => void;
  parent: ParentDetails;
  studentId: string;
}

const addressField = (value?: string | null) => value || '';

const normalizeRelation = (value?: string | null) =>
  (value || '').trim().toLowerCase();

const onInvalidParentForm = () => {
  toast.error('Please fix the highlighted fields before submitting.');
};

export function EditParentForm({
  parent,
  onCancel,
  studentId,
}: EditParentFormProps) {
  const { mutate: updateParent, isPending } = useUpdateParentByStudentId();
  const { data: currencyData } = useGetCurrency();
  const defaultCountry = getCountryCodeFromCurrency(currencyData?.name);

  const form = useForm<ParentFormValues>({
    resolver: zodResolver(parentSchema),
    defaultValues: {
      firstName: parent.firstName || '',
      lastName: parent.lastName || '',
      email: parent.email || '',
      primaryPhone: parent.primaryPhone || '',
      familyName: parent.familyName || '',
      relation: normalizeRelation(parent.relation),
      address: {
        street: addressField(parent.address?.street),
        city: addressField(parent.address?.city),
        state: addressField(parent.address?.state),
        country: addressField(parent.address?.country),
        zip: addressField(parent.address?.zip),
      },
    },
  });

  const onSubmit = (data: ParentFormValues) => {
    const updateData: Partial<ParentDetails> = {};
    const existingAddress = parent.address || {
      street: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    };

    if (data.firstName !== parent.firstName) {
      updateData.firstName = data.firstName;
    }
    if (data.lastName !== parent.lastName) {
      updateData.lastName = data.lastName;
    }
    if (data.email !== parent.email) {
      updateData.email = data.email;
    }
    if (data.primaryPhone !== parent.primaryPhone) {
      updateData.primaryPhone = data.primaryPhone;
    }
    if (data.familyName !== parent.familyName) {
      updateData.familyName = data.familyName;
    }
    if (data.relation !== normalizeRelation(parent.relation)) {
      updateData.relation = data.relation;
    }

    const nextAddress = {
      street: data.address.street || '',
      city: data.address.city || '',
      state: data.address.state || '',
      country: data.address.country || '',
      zip: data.address.zip || '',
    };

    const addressChanged =
      nextAddress.street !== addressField(existingAddress.street) ||
      nextAddress.city !== addressField(existingAddress.city) ||
      nextAddress.state !== addressField(existingAddress.state) ||
      nextAddress.country !== addressField(existingAddress.country) ||
      nextAddress.zip !== addressField(existingAddress.zip);

    if (addressChanged) {
      updateData.address = nextAddress;
    }

    if (Object.keys(updateData).length === 0) {
      onCancel();
      return;
    }

    updateParent(
      {
        studentId,
        parentId: parent._id,
        parent: updateData,
      },
      {
        onSuccess: () => {
          toast.success('Parent details updated');
          onCancel();
        },
        onError: () => {
          toast.error('Failed to update parent details. Please try again.');
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onInvalidParentForm)}
        className='space-y-2'
      >
        <div className='grid grid-cols-2 gap-4'>
          <FormField
            control={form.control}
            name='firstName'
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Enter name' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='lastName'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Enter name' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* <div className='grid grid-cols-2 gap-4'></div> */}
          <FormField
            control={form.control}
            name='familyName'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Family Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Enter family name' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='primaryPhone'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile</FormLabel>
                <FormControl>
                  {/* <Input {...field} placeholder='Enter mobile number' /> */}
                  <PhoneInput
                    country={defaultCountry}
                    disableCountryGuess={true}
                    value={field.value || ''}
                    onChange={(value) => {
                      field.onChange(phoneInputValueToE164(value));
                    }}
                    inputStyle={{ width: '100%' }}
                    enableSearch={true}
                    searchPlaceholder='Search country...'
                    searchNotFound='No country found'
                    enableAreaCodes={true}
                    countryCodeEditable={true}
                    placeholder='Enter mobile number'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={true}
                    type='email'
                    placeholder='Enter email'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='relation'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Relation</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select relation' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='mother'>Mother</SelectItem>
                    <SelectItem value='father'>Father</SelectItem>
                    <SelectItem value='guardian'>Guardian</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className='space-y-2'>
          <h3 className='text-lg font-semibold'>Address</h3>
          <FormField
            control={form.control}
            name='address.street'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Enter street address' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className='grid grid-cols-2 gap-4'>
            <FormField
              control={form.control}
              name='address.city'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Enter city' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='address.state'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Enter state' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <FormField
              control={form.control}
              name='address.country'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Enter country' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='address.zip'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP Code</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Enter ZIP code' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className='flex items-center justify-between'>
          <div></div>
          <div className='flex items-center gap-4'>
            <Button
              variant={'outline'}
              type='button'
              className='mt-2 w-full'
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button type='submit' className='mt-2 w-full' disabled={isPending}>
              {isPending ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
